import {
  DIAGNOSIS_CRITICALITY_LABELS,
  DIAGNOSIS_SCALE_MAX,
  DIAGNOSIS_SCALE_MIN,
  DIAGNOSIS_STATUS_LABELS,
  diagnosisItemSchema,
  formatDate,
  initiativeOptionsSchema,
  initiativeSchema,
  type DiagnosisCriticalityName,
  type DiagnosisItem,
  type DiagnosisStatusName,
  type InitiativeInput,
  type TrafficLight,
} from '@fluxus/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link2, Target, Unlink } from 'lucide-react';
import { useEffect, useId, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { StatusBadge } from '@/components/status/StatusBadge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { FormField, fieldA11y } from '@/components/ui/form';
import { NativeSelect } from '@/components/ui/select';
import { errorMessage } from '@/lib/api/errors';
import { useAuth, useCan } from '@/lib/auth/session';
import { InitiativeForm } from '@/pages/IniciativasPage';

/**
 * Partes do item de diagnóstico usadas no Diagnóstico (T-160b) e na Matriz de priorização (T-161b; DEC-061, DEC-062):
 * cores, escala, o resumo do item, relacionar a objetivo e transformar em iniciativa.
 */

/** Cores da criticidade e do status do item. */
export const CRITICALITY_LIGHT: Record<DiagnosisCriticalityName, TrafficLight> = {
  LOW: 'gray',
  MEDIUM: 'yellow',
  HIGH: 'red',
  CRITICAL: 'red',
};
export const STATUS_LIGHT: Record<DiagnosisStatusName, TrafficLight> = {
  IDENTIFIED: 'gray',
  IN_ANALYSIS: 'blue',
  IN_MATRIX: 'blue',
  IN_TREATMENT: 'yellow',
  MONITORED: 'green',
  ARCHIVED: 'gray',
};
export const SCALE = Array.from(
  { length: DIAGNOSIS_SCALE_MAX - DIAGNOSIS_SCALE_MIN + 1 },
  (_, i) => DIAGNOSIS_SCALE_MIN + i,
);
export const SCALE_LABELS: Record<number, string> = {
  1: '1 — muito baixo',
  2: '2 — baixo',
  3: '3 — médio',
  4: '4 — alto',
  5: '5 — muito alto',
};

export function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 font-medium break-words">{children}</dd>
    </div>
  );
}

/** Criticidade e status do item. */
export function ItemBadges({ item }: { item: DiagnosisItem }) {
  return (
    <>
      <StatusBadge
        status={CRITICALITY_LIGHT[item.criticality]}
        label={`Criticidade ${DIAGNOSIS_CRITICALITY_LABELS[item.criticality].toLowerCase()}`}
      />
      <StatusBadge status={STATUS_LIGHT[item.status]} label={DIAGNOSIS_STATUS_LABELS[item.status]} />
    </>
  );
}

/**
 * O item por inteiro: campos do §11.4, o arquivamento (quando houver) e o que se liga a ele — objetivo, iniciativa e
 * planos, com o nome só para quem enxerga.
 */
export function ItemSummary({
  item: d,
  organizationId,
  projectId,
}: {
  item: DiagnosisItem;
  organizationId: string;
  projectId: string;
}) {
  const canDo = useCan();
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <ItemBadges item={d} />
        {d.sentToMatrixAt ? (
          <span className="text-xs text-muted-foreground">
            Enviado para a matriz em {formatDate(d.sentToMatrixAt.slice(0, 10))}
          </span>
        ) : null}
      </div>
      {d.archive ? (
        <p className="rounded-md border border-border bg-muted px-3 py-2" role="note">
          Arquivado em {formatDate(d.archive.at.slice(0, 10))} por {d.archive.byName}: {d.archive.reason}
        </p>
      ) : null}
      {d.description ? <p className="whitespace-pre-line">{d.description}</p> : null}
      <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-3">
        <Fact label="Área">{d.department.name}</Fact>
        <Fact label="Unidade">{d.businessUnit?.name ?? 'Sem unidade'}</Fact>
        <Fact label="Responsável pelo levantamento">{d.surveyedBy?.name ?? 'Não informado'}</Fact>
        <Fact label="Impacto">{SCALE_LABELS[d.impact]}</Fact>
        <Fact label="Urgência">{SCALE_LABELS[d.urgency]}</Fact>
        <Fact label="Esforço estimado">{SCALE_LABELS[d.effort]}</Fact>
      </dl>
      <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
        <Fact label="Evidência">
          <span className="whitespace-pre-line">{d.evidence ?? 'Sem evidência descrita'}</span>
        </Fact>
        <Fact label="Observações">
          <span className="whitespace-pre-line">{d.notes ?? 'Sem observações'}</span>
        </Fact>
      </dl>
      <section aria-label="Vínculos do item" className="flex flex-col gap-3">
        <h3 className="font-bold">Objetivo, iniciativa e planos</h3>
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
          <Fact label="Objetivo relacionado">
            {d.objective ? (
              d.objective.name === null ? (
                'Objetivo fora do seu acesso'
              ) : canDo('objetivos', 'view') ? (
                <Link
                  className="inline-flex items-center underline underline-offset-2"
                  to={`/empresas/${organizationId}/estrategia/objetivos/${d.objective.id}?projeto=${projectId}`}
                >
                  {d.objective.name}
                  {d.objective.archived ? ' (arquivado)' : ''}
                </Link>
              ) : (
                d.objective.name
              )
            ) : (
              'Nenhum'
            )}
          </Fact>
          <Fact label="Iniciativa">
            {d.initiative ? (
              d.initiative.name === null ? (
                'Iniciativa fora do seu acesso'
              ) : (
                <Link
                  className="inline-flex items-center underline underline-offset-2"
                  to={`/empresas/${organizationId}/execucao/iniciativas?projeto=${projectId}&iniciativa=${d.initiative.id}`}
                >
                  {d.initiative.name}
                  {d.initiative.archived ? ' (arquivada)' : ''}
                </Link>
              )
            ) : (
              'Ainda não virou iniciativa'
            )}
          </Fact>
        </dl>
        <div>
          <p className="text-xs text-muted-foreground">Planos de ação gerados</p>
          {d.plans.length ? (
            <ul aria-label="Planos de ação do item" className="mt-1 flex flex-col gap-1 font-medium">
              {d.plans.map((p) => (
                <li key={p.id}>
                  {p.name === null ? 'Plano fora do seu acesso' : p.name}
                  {p.archived ? ' (arquivado)' : ''}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-0.5 font-medium">Nenhum plano ainda</p>
          )}
        </div>
      </section>
    </>
  );
}

/** Relacionar o item a um objetivo existente (e desfazer). */
export function RelateObjective({
  base,
  item: d,
  objectives,
  onChanged,
}: {
  /** Rotas do diagnóstico do projeto. */
  base: string;
  item: DiagnosisItem;
  objectives: { id: string; name: string }[];
  onChanged: () => Promise<unknown>;
}) {
  const { client } = useAuth();
  const [objectiveId, setObjectiveId] = useState('');
  const id = useId();
  // A escolha começa no relacionado (ou vazia) a cada item aberto.
  useEffect(() => setObjectiveId(d.objective?.id ?? ''), [d.id, d.objective?.id]);
  const relate = useMutation({
    meta: { handlesError: true },
    mutationFn: (objective: string | null) =>
      client.request(`${base}/${d.id}/objective`, {
        method: 'PUT',
        body: { objectiveId: objective },
        schema: diagnosisItemSchema,
      }),
    onSuccess: async (saved) => {
      toast.success(saved.objective ? 'Objetivo relacionado' : 'Relação com o objetivo desfeita', {
        description: saved.title,
      });
      await onChanged();
    },
    onError: (err) => toast.error('Não foi possível relacionar', { description: errorMessage(err) }),
  });
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
      <div className="flex-1">
        <FormField
          id={id}
          label="Relacionar a um objetivo existente"
          hint={
            objectives.length ? undefined : (
              <p className="text-xs text-muted-foreground">
                Não há objetivos ativos no seu escopo neste projeto.
              </p>
            )
          }
        >
          <NativeSelect
            {...fieldA11y(id, undefined, !objectives.length)}
            value={objectiveId}
            onChange={(e) => setObjectiveId(e.target.value)}
          >
            <option value="">Escolha o objetivo</option>
            {objectives.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </NativeSelect>
        </FormField>
      </div>
      <Button
        variant="outline"
        disabled={!objectiveId || objectiveId === d.objective?.id}
        loading={relate.isPending && relate.variables !== null}
        onClick={() => relate.mutate(objectiveId)}
      >
        <Link2 aria-hidden />
        Relacionar
      </Button>
      {d.objective ? (
        <Button
          variant="ghost"
          loading={relate.isPending && relate.variables === null}
          onClick={() => relate.mutate(null)}
        >
          <Unlink aria-hidden />
          Desfazer relação
        </Button>
      ) : null}
    </div>
  );
}

/** Transformar o item em iniciativa: o cadastro da iniciativa já preenchido com o título, a descrição e o objetivo. */
export function TransformInitiativeDialog({
  organizationId,
  projectId,
  base,
  item: d,
  open,
  onClose,
  onDone,
}: {
  organizationId: string;
  projectId: string;
  base: string;
  item: DiagnosisItem | undefined;
  open: boolean;
  onClose: () => void;
  onDone: () => Promise<unknown>;
}) {
  const { client } = useAuth();
  const [busy, setBusy] = useState(false);
  const options = useQuery({
    queryKey: ['initiatives', projectId, 'options'],
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}/projects/${projectId}/initiatives/options`, {
        schema: initiativeOptionsSchema,
        signal,
      }),
    enabled: open,
  });
  async function transform(input: InitiativeInput) {
    const created = await client.request(`${base}/${d!.id}/initiative`, {
      method: 'POST',
      body: input,
      schema: initiativeSchema,
    });
    onClose();
    toast.success('Iniciativa criada a partir do item', { description: created.name });
    await onDone();
  }
  return (
    <Dialog
      open={open && d !== undefined}
      onOpenChange={(next) => !next && onClose()}
      busy={busy}
      title="Transformar em iniciativa"
      description={d ? `A partir do item: ${d.title}` : undefined}
      className="w-[min(46rem,calc(100vw-2rem))]"
    >
      {options.isPending ? (
        <LoadingState rows={4} label="Carregando os objetivos e as pessoas" />
      ) : options.isError ? (
        <ErrorState
          error={options.error}
          onRetry={() => void options.refetch()}
          retrying={options.isFetching}
        />
      ) : d && options.data.objectives.length === 0 ? (
        <EmptyState
          icon={Target}
          title="Nenhum objetivo no seu escopo"
          description="A iniciativa se liga a um objetivo estratégico: cadastre os objetivos do projeto primeiro."
        />
      ) : d ? (
        <InitiativeForm
          options={options.data}
          prefill={{ name: d.title, description: d.description, objectiveId: d.objective?.id ?? null }}
          submitLabel="Criar iniciativa a partir do item"
          onSubmit={transform}
          onCancel={onClose}
          onBusyChange={setBusy}
        />
      ) : null}
    </Dialog>
  );
}
